import React from 'react';
import { useUser } from '../context/UserContext';

// Higher-Order Component to check roles
export const withRoleAccess = (WrappedComponent, roleNames) => {
  return (props) => {
    const { user, loading, hasRole } = useUser();

    if (loading) {
      return <div>Loading...</div>; // You can replace this with a spinner
    }

    if (!user) {
      return <div>Access denied. Please log in.</div>;
    }

    if (!hasRole(roleNames)) {
      return <div>Access denied. You don't have the required role(s).</div>;
    }

    return <WrappedComponent {...props} />;
  };
};

// Higher-Order Component to check permissions
export const withPermissionAccess = (WrappedComponent, permissionNames) => {
  return (props) => {
    const { user, loading, hasPermission } = useUser();

    if (loading) {
      return <div>Loading...</div>; // You can replace this with a spinner
    }

    if (!user) {
      return <div>Access denied. Please log in.</div>;
    }

    if (!hasPermission(permissionNames)) {
      return <div>Access denied. You don't have the required permission(s).</div>;
    }

    return <WrappedComponent {...props} />;
  };
};

// Component to conditionally render based on roles
export const RoleGuard = ({ roles, fallback = null, children }) => {
  const { user, loading, hasRole } = useUser();

  if (loading) {
    return <div>Loading...</div>; // You can replace this with a spinner
  }

  if (!user) {
    return fallback;
  }

  if (!hasRole(roles)) {
    return fallback;
  }

  return children;
};

// Component to conditionally render based on permissions
export const PermissionGuard = ({ permissions, fallback = null, children }) => {
  const { user, loading, hasPermission } = useUser();

  if (loading) {
    return <div>Loading...</div>; // You can replace this with a spinner
  }

  if (!user) {
    return fallback;
  }

  if (!hasPermission(permissions)) {
    return fallback;
  }

  return children;
};

/**
 * Checks if a user has a specific permission, supporting wildcard matching.
 * @param {Array<string>} userPermissions - The list of permissions the user has.
 * @param {string} requiredPermission - The permission to check for (e.g., 'fundedhere:crud', 'fundedhere:*').
 * @returns {boolean} - True if the user has the required permission, false otherwise.
 */
export const checkWildcardPermission = (userPermissions, requiredPermission) => {
  if (!userPermissions || userPermissions.length === 0) {
    return false;
  }

  // If the required permission is a wildcard (e.g., 'module:*')
  if (requiredPermission.endsWith(':*')) {
    const basePermission = requiredPermission.slice(0, -1); // 'module:'
    const result = userPermissions.some(p => p.startsWith(basePermission));
    return result;
  }

  // For exact permission match
  const result = userPermissions.includes(requiredPermission);
  return result;
};